import { Box, CardActions, CardContent } from "@mui/material";
import * as React from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { ITypedOption } from "../../../models/common/typedOption";
import { ICompanyEditItem } from "../../../models/company/companyEditItem";
import * as companyService from "../../../models/services/companyService";
import { IAppSessionState } from "../../../models/store/storeTypes";
import { IUnitNodePermissions } from "../../../models/unit/unitNodePermissions";
import { HeaderMenuItem, NavigationMenu } from "../../app/NavigationMenu";
import { UserMenu } from "../../app/UserMenu";
import { ConfirmationButton } from "../../framework/Button/ConfirmationButton";
import { SettingsButton } from "../../framework/Button/SettingsButton";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { PageContainer } from "../../framework/Page/PageContainer";
import { PageContent } from "../../framework/Page/PageContent";
import { PageTabs } from "../../framework/Page/PageTabs";
import { PageTitle } from "../../framework/Page/PageTitle";
import { PageTopBar } from "../../framework/Page/PageTopBar";
import { UnitPermissionDialog } from "../../unit/Unit/UnitPermissionDialog";
import { CompanyEdit as CompanyEditView, CompanyEditViewMode } from "../CompanyEdit";

/* eslint-disable no-unused-vars */
const enum CompanyPageEditMenuItem {
    None = "",
    General = "1",
    Units = "2",
}
/* eslint-enable no-unused-vars */

// CompanyPageEdit
export interface ICompanyPageEditProps {
    appState: IAppSessionState;
    dataId: number;
    onLogout: () => void;
    onSetCulture: (culture: string) => void;
    onSaveUserParameters: (parameters: ITypedOption[]) => void;
    onRefreshSessionState: () => void;
}

interface ICompanyPageEditState {
    showNavPanel: boolean;
    activeMenuItem: CompanyPageEditMenuItem;
    settingsActive: boolean;
    company: ICompanyEditItem;
    // Properties
    showUnitNodePermissionsDialog: boolean;
    unitNodePermissions: IUnitNodePermissions;
}

export class CompanyPageEdit extends React.Component<ICompanyPageEditProps, ICompanyPageEditState> {
    private companyEditViewRef = React.createRef<CompanyEditView>();

    constructor(props: ICompanyPageEditProps) {
        super(props);
        const sessionState = props.appState.state;
        this.state = {
            showNavPanel: sessionState.getShowPageNavigation(),
            activeMenuItem: CompanyPageEditMenuItem.General,
            settingsActive: false,
            showUnitNodePermissionsDialog: false,
            unitNodePermissions: null,
            company: null
        };
    }

    handleToggleShowNavPanel = () => {
        this.setState({ showNavPanel: !this.state.showNavPanel });
    };

    handleToggleActiveMenuItem = (userPageMenuItem: CompanyPageEditMenuItem) => {
        this.setState({
            activeMenuItem: this.state.activeMenuItem !== userPageMenuItem ? userPageMenuItem : CompanyPageEditMenuItem.None,
            settingsActive: false
        });
    };

    handleSave = async () => {
        if (this.companyEditViewRef && this.companyEditViewRef.current) {
            this.companyEditViewRef.current.save();
        }
    };

    handleNavigateOutCheck = async (): Promise<boolean> => {
        if (this.companyEditViewRef && this.companyEditViewRef.current) {
            return await this.companyEditViewRef.current.canNavigateOut();
        } else {
            return true;
        }
    };

    handleCompanyLoaded = (company: ICompanyEditItem) => {
        this.setState({
            company
        });
    };

    handleOpenPermissions = async () => {
        const sessionState = this.props.appState.state;
        const result = await AppUtils.callService(() => companyService.getUnitNodePermissions(sessionState.companyId), false);
        if (!result) return;
        this.setState({
            showUnitNodePermissionsDialog: true,
            unitNodePermissions: result
        });
    };

    handlePermissionsDialogOk = () => {
        this.setState({
            showUnitNodePermissionsDialog: false
        });
    };

    handlePermissionsDialogCancel = () => {
        this.setState({
            showUnitNodePermissionsDialog: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        const companyName = state.company ? state.company.name : "";
        return (
            <PageContainer>
                <NavigationMenu
                    appState={props.appState}
                    activeMenuItem={HeaderMenuItem.Companies}
                    activeCompanyId={sessionState.companyId}
                />
                <PageContent>
                    <PageTopBar>
                        <UserMenu
                            appState={props.appState}
                            onLogout={props.onLogout}
                            onSetCulture={props.onSetCulture}
                            onRefreshSessionState={props.onRefreshSessionState}
                        />
                    </PageTopBar>
                    <>
                        <PageTitle title={Translations.Company + (companyName ? " - " + companyName : "")}>
                            {sessionState.isCompanyAdmin &&
                                <SettingsButton onClick={this.handleOpenPermissions}>
                                    {Translations.Settings}
                                </SettingsButton>
                            }
                        </PageTitle>
                        <MainListContainer>
                            <PageTabs
                                activeTabId={state.activeMenuItem}
                                tabs={[
                                    { id: CompanyPageEditMenuItem.General, title: Translations.BasicInformation }
                                ]}
                                changeTab={this.handleToggleActiveMenuItem}
                            />
                            <CardContent>
                                <Box sx={{ height: "calc(100vh - 296px)", overflowX: "hidden", overflowY: "auto" }}>
                                    <CompanyEditView
                                        ref={this.companyEditViewRef}
                                        onDialog={false}
                                        sessionState={sessionState}
                                        companyId={sessionState.companyId}
                                        viewMode={CompanyEditViewMode.General}
                                        onRefreshSessionState={props.onRefreshSessionState}
                                        onCompanyLoaded={this.handleCompanyLoaded}
                                    />
                                </Box>
                            </CardContent>
                            <CardActions sx={{ justifyContent: "flex-end", paddingX: "1rem" }}>
                                <ConfirmationButton onClick={this.handleSave}>
                                    {Translations.Save}
                                </ConfirmationButton>
                            </CardActions>
                        </MainListContainer>
                    </>
                </PageContent>
                {state.showUnitNodePermissionsDialog &&
                    <UnitPermissionDialog
                        companyId={sessionState.companyId}
                        unitNodePermissions={state.unitNodePermissions}
                        onSave={companyService.saveUnitNodePermissions}
                        onOk={this.handlePermissionsDialogOk}
                        onCancel={this.handlePermissionsDialogCancel}
                    />
                }
            </PageContainer>
        );
    }
}